// EvidenceLinksDisplay — read-only render of evidence links on the
// contract details surface. Edits flow through the existing Edit
// details page; this component keeps the details surface honest about
// being a viewer (no inline editing). Brand-coloured link text follows
// the platform text-link convention and is exempt from the brand-violet
// density rule (links communicate clickability).

import { ExternalLink as ExternalLinkIcon } from "lucide-react";
import type { ExternalLink } from "../../../types";
import { safeExternalHref } from "../../../utils/evidenceLinks";

interface EvidenceLinksDisplayProps {
  links: ExternalLink[];
}

export function EvidenceLinksDisplay({ links }: EvidenceLinksDisplayProps) {
  if (!links || links.length === 0) {
    return (
      <p className="text-caption italic text-slate-500 dark:text-slate-400">
        No evidence links recorded
      </p>
    );
  }

  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-2">
      {links.map((link, idx) => {
        const href = safeExternalHref(link.url);
        const text =
          link.label && link.label.length > 0 ? link.label : link.url;
        return (
          <li key={`${link.url}-${idx}`}>
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-x-1 text-body-sm font-medium text-brand-700 dark:text-brand-300 hover:text-brand-600 dark:hover:text-brand-200 hover:underline"
              >
                {text}
                <ExternalLinkIcon className="h-3 w-3" aria-hidden="true" />
              </a>
            ) : (
              <span className="text-body-sm font-medium text-slate-700 dark:text-slate-300">
                {text}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default EvidenceLinksDisplay;
